/**
 * 로그아웃 처리 유틸리티
 *
 * localStorage의 인증 관련 키들을 모두 제거하고,
 * 서비스 타입에 맞는 로그인 진입 경로를 반환한다.
 */

const AUTH_KEYS = [
  'accessToken',
  'token',
  'userId',
  'userEmail',
  'userName',
  'serviceType',
  'adminRole',
  'myCountries',
  'signStatus',
  'verifiedClientId',
  'verifiedClientName',
  'verifiedServiceType',
  'verifiedInviteCode',
  'activeClientId',
  'savedPassword',
  'autoLogin',
  'userRole',
  'mustChangePassword',
];

/**
 * 현재 세션의 access token을 반환한다.
 * 일반 로그인은 sessionStorage, 「로그인 유지하기」는 localStorage를 사용한다.
 * (화면 문구가 「자동 로그인」이었는데 하는 일과 달라 2026-08-01 에 바꿨다 —
 *  비밀번호를 저장하지 않으므로 자동으로 로그인해 주지는 못한다.)
 */
export const getAccessToken = () => {
  return sessionStorage.getItem('accessToken') || localStorage.getItem('accessToken');
};

/**
 * 지금 이 세션으로 화면을 그릴 수 있는가.
 *
 * 토큰만 보고 판단하면 안 된다 — **토큰은 있는데 `serviceType` 이 없는 반쪽 상태**가 있다.
 * 그러면 라우트 둘이 서로 밀어내며 무한 리다이렉트가 나고 화면이 하얗게 남는다:
 *   · ProtectedRoute(/dashboard) — 권한이 없다 → `/` 로 보냄
 *   · GuestOnlyRoute(/)          — 토큰이 있다 → `/dashboard` 로 보냄
 * (2026-08-01 실제로 발생. 콘솔에 「Maximum update depth exceeded」가 쌓인다.)
 *
 * 반쪽 상태가 되는 길: 로그인 응답이 도중에 끊겨 토큰만 저장되거나,
 * 다른 탭에서 로그아웃해 사용자 정보만 지워지거나, 사람이 저장소를 손대거나.
 *
 * serviceType 이 없으면 어차피 권한을 못 정해 아무 화면도 못 그린다.
 * 그래서 **못 쓰는 세션 = 로그인 안 된 것**으로 본다 — 로그인 화면으로 깨끗이 내려간다.
 */
export const hasUsableSession = () => {
  if (!getAccessToken()) return false;
  return !!localStorage.getItem('serviceType');
};

/**
 * 비밀번호를 저장하지 않고 로그인 유지 범위만 선택해 토큰을 저장한다.
 */
export const storeAccessToken = (token, persistent = false) => {
  localStorage.removeItem('accessToken');
  sessionStorage.removeItem('accessToken');

  if (!token) return;
  const storage = persistent ? localStorage : sessionStorage;
  storage.setItem('accessToken', token);
};

/**
 * 직전 사용자의 serviceType에 맞는 로그아웃 후 진입 경로 반환
 * - INFLUENCER → /login (인플루언서 진입점)
 * - 그 외 (MASTER/MANAGER/CLIENT/DELIVERY_*) → / (어드민/클라이언트 진입점)
 */
export const getLogoutRedirect = (serviceType) => {
  const type = serviceType || localStorage.getItem('serviceType');
  return type === 'INFLUENCER' ? '/login' : '/';
};

/**
 * 인증 관련 localStorage 키만 제거 (개인 설정 등은 보존)
 */
export const clearAuthStorage = () => {
  AUTH_KEYS.forEach((k) => {
    localStorage.removeItem(k);
    sessionStorage.removeItem(k);
  });
};

/**
 * 로그아웃 + 적절한 진입점 경로 반환
 * (실제 navigate/리다이렉트는 호출부에서 수행)
 */
export const performLogout = () => {
  const target = getLogoutRedirect();
  clearAuthStorage();
  return target;
};

const SESSION_SNAPSHOT_KEY = '__sessionSnapshot';

/**
 * 테스트 계정으로 전환하기 전, 현재 세션(로그인 정보)을 스냅샷으로 저장한다.
 * sessionStorage에 저장하므로 탭을 닫으면 사라진다(전환 중 실수로 남는 걸 방지).
 */
export const snapshotCurrentSession = () => {
  const snapshot = {};
  AUTH_KEYS.forEach((k) => {
    snapshot[k] = { local: localStorage.getItem(k), session: sessionStorage.getItem(k) };
  });
  sessionStorage.setItem(SESSION_SNAPSHOT_KEY, JSON.stringify(snapshot));
};

export const hasSessionSnapshot = () => sessionStorage.getItem(SESSION_SNAPSHOT_KEY) != null;

/**
 * snapshotCurrentSession으로 저장해둔 이전 세션으로 복원한다.
 * @returns {boolean} 복원할 스냅샷이 있었으면 true
 */
export const restoreSessionSnapshot = () => {
  const raw = sessionStorage.getItem(SESSION_SNAPSHOT_KEY);
  if (!raw) return false;

  const snapshot = JSON.parse(raw);
  clearAuthStorage();
  AUTH_KEYS.forEach((k) => {
    const entry = snapshot[k];
    if (entry?.local != null) localStorage.setItem(k, entry.local);
    if (entry?.session != null) sessionStorage.setItem(k, entry.session);
  });
  sessionStorage.removeItem(SESSION_SNAPSHOT_KEY);
  return true;
};
